'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@repo/ui/lib/utils'
import { Button } from '@repo/ui/components/ui/button'
import { Input } from '@repo/ui/components/ui/input'
import { Textarea } from '@repo/ui/components/ui/textarea'

const inputGroupBaseClasses = cn(
  'group/input-group relative flex w-full items-center',
  'rounded-lg transition-colors outline-none',
)

const inputGroupStructureClasses = cn(
  'has-[>textarea]:h-auto',
  'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-end]]:flex-col',
  'has-[>[data-align=block-start]]:h-auto has-[>[data-align=block-start]]:flex-col',
)

const inputGroupInputPaddingClasses = cn(
  'has-[>[data-align=block-end]]:[&>input]:pt-3',
  'has-[>[data-align=block-start]]:[&>input]:pb-3',
  'has-[>[data-align=inline-end]]:[&>input]:pr-1.5',
  'has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
)

const inputGroupStateClasses = cn(
  'has-disabled:bg-input/50 has-disabled:opacity-50',
  'dark:has-disabled:bg-input/80',
  'in-data-[slot=combobox-content]:focus-within:border-inherit',
  'in-data-[slot=combobox-content]:focus-within:ring-0',
  'has-[[data-slot=input-group-control]:focus-visible]:border-ring',
  'has-[[data-slot=input-group-control]:focus-visible]:ring-3',
  'has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
  'has-[[data-slot][aria-invalid=true]]:border-destructive',
  'has-[[data-slot][aria-invalid=true]]:ring-3',
  'has-[[data-slot][aria-invalid=true]]:ring-destructive/20',
  'dark:has-[[data-slot][aria-invalid=true]]:ring-destructive/40',
)

const inputGroupVariants = cva(
  cn(
    inputGroupBaseClasses,
    inputGroupStructureClasses,
    inputGroupInputPaddingClasses,
    inputGroupStateClasses,
  ),
  {
    defaultVariants: {
      variant: 'default',
    },
    variants: {
      variant: {
        default: cn(
          'border border-input shadow-xs',
          'dark:bg-input/30',
          'has-[[data-slot=input-group-control]:focus-visible]:ring-1',
          'has-[[data-slot=input-group-control]:focus-visible]:ring-ring',
        ),
        ghost: cn(
          'bg-transparent shadow-none',
          'hover:bg-accent dark:hover:bg-accent/50',
          'has-[[data-slot=input-group-control]:focus-visible]:bg-accent',
          'dark:has-[[data-slot=input-group-control]:focus-visible]:bg-accent/50',
        ),
      },
    },
  },
)

function InputGroup({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupVariants>) {
  return (
    <div
      className={cn(inputGroupVariants({ variant }), className)}
      data-slot="input-group"
      role="group"
      {...props}
    data-sentry-component="InputGroup" data-sentry-source-file="input-group.tsx" />
  )
}

const inputGroupAddonBaseClasses = cn(
  'flex h-auto cursor-text items-center justify-center gap-2 py-1.5',
  'text-sm font-medium text-muted-foreground select-none',
  'group-data-[disabled=true]/input-group:opacity-50',
  "[&>kbd]:rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-4",
)

const inputGroupAddonVariants = cva(inputGroupAddonBaseClasses, {
  defaultVariants: {
    align: 'inline-start',
  },
  variants: {
    align: {
      'block-end': cn(
        'order-last w-full justify-start px-2.5 pb-2',
        'group-has-[>input]/input-group:pb-2 [.border-t]:pt-2',
      ),
      'block-start': cn(
        'order-first w-full justify-start px-2.5 pt-2',
        'group-has-[>input]/input-group:pt-2 [.border-b]:pb-2',
      ),
      'inline-end': cn('order-last pr-2', 'has-[>button]:mr-[-0.3rem] has-[>kbd]:mr-[-0.15rem]'),
      'inline-start': cn('order-first pl-2', 'has-[>button]:ml-[-0.3rem] has-[>kbd]:ml-[-0.15rem]'),
    },
  },
})

function InputGroupAddon({
  align = 'inline-start',
  className,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) {
  return (
    // Like a <label>, a click focuses the input, which keyboard users reach directly.
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className={cn(inputGroupAddonVariants({ align }), className)}
      data-align={align}
      data-slot="input-group-addon"
      onClick={e => {
        if (e.target instanceof Element && e.target.closest('button')) {
          return
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus()
      }}
      role="group"
      {...props}
    data-sentry-component="InputGroupAddon" data-sentry-source-file="input-group.tsx" />
  )
}

const inputGroupButtonVariants = cva(cn('flex items-center gap-2 text-sm shadow-none'), {
  defaultVariants: {
    size: 'xs',
  },
  variants: {
    size: {
      'icon-sm': cn('size-8 p-0', 'has-[>svg]:p-0'),
      'icon-xs': cn('size-6 rounded-[calc(var(--radius)-3px)] p-0', 'has-[>svg]:p-0'),
      sm: '',
      xs: cn(
        'h-6 gap-1 rounded-[calc(var(--radius)-3px)] px-1.5',
        "[&>svg:not([class*='size-'])]:size-3.5",
      ),
    },
  },
})

function InputGroupButton({
  className,
  size = 'xs',
  type = 'button',
  variant = 'ghost',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'reset' | 'submit'
  }) {
  return (
    <Button
      className={cn(inputGroupButtonVariants({ size }), className)}
      data-size={size}
      type={type}
      variant={variant}
      {...props}
    data-sentry-element="Button" data-sentry-component="InputGroupButton" data-sentry-source-file="input-group.tsx" />
  )
}

function InputGroupInput({ className, ...props }: React.ComponentProps<'input'>) {
  return (
    <Input
      className={cn(
        'flex-1',
        'border-0 bg-transparent shadow-none ring-0',
        'dark:bg-transparent',
        'focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:disabled:bg-transparent',
        className,
      )}
      data-slot="input-group-control"
      {...props}
    data-sentry-element="Input" data-sentry-component="InputGroupInput" data-sentry-source-file="input-group.tsx" />
  )
}

function InputGroupText({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn(
        'flex items-center gap-2',
        'text-sm text-muted-foreground',
        "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    data-sentry-component="InputGroupText" data-sentry-source-file="input-group.tsx" />
  )
}

function InputGroupTextarea({ className, ...props }: React.ComponentProps<'textarea'>) {
  return (
    <Textarea
      className={cn(
        'flex-1 resize-none rounded-none py-2',
        'border-0 bg-transparent shadow-none ring-0',
        'dark:bg-transparent',
        'focus-visible:ring-0 disabled:bg-transparent aria-invalid:ring-0 dark:disabled:bg-transparent',
        className,
      )}
      data-slot="input-group-control"
      {...props}
    data-sentry-element="Textarea" data-sentry-component="InputGroupTextarea" data-sentry-source-file="input-group.tsx" />
  )
}

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
}
